import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Pagination,
  Tabs,
  Tab,
  Badge,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Menu,
  ButtonGroup,
} from '@mui/material';
import {
  Home,
  ShoppingBag,
  Visibility,
  LocalShipping,
  CheckCircle,
  Cancel,

  Download,
  Search,
  FilterList,
  Receipt,
  Support,
  Star,
  StarBorder,
  Message,
  Phone,
  Email,
  LocationOn,
  Schedule,
  Payment,
  Inventory,
  TrendingUp,
  Assignment,
  AttachMoney,
  Pending,
  Done,
  Error,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

import {
  fetchCustomerOrders,
  trackOrder as trackCustomerOrder,
  cancelOrder as cancelCustomerOrder,
  createReview,
  clearCustomerError
} from '../store/customerSlice';
import { orderService } from '../services/orderService';
import { getProductPrimaryImageUrl } from '../utils/imageUtils';

const OrdersPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { orders, loading, trackingInfo } = useSelector((state) => state.orders);
  const {
    orders: customerOrders,
    trackingInfo: customerTrackingInfo,
    ordersLoading,
    trackingLoading,
    loading: customerLoading,
    error: customerError
  } = useSelector((state) => state.customer);
  
  const [activeTab, setActiveTab] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');

  const [fetchError, setFetchError] = useState(null);
  const [orderStats, setOrderStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    cancelled: 0
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOrder, setMenuOrder] = useState(null);
  const [orderDetailsDialogOpen, setOrderDetailsDialogOpen] = useState(false);

  // Fetch orders with filters
  useEffect(() => {
    if (user?.id) {
      const fetchParams = { 
        customerId: user.id, 
        page: currentPage, 
        size: 10 
      };
      
      // Add status filter to API call if not 'all'
      if (statusFilter !== 'all') {
        fetchParams.status = statusFilter.toUpperCase();
      }
      
      if (searchTerm) {
        fetchParams.search = searchTerm;
      }
      
      dispatch(fetchCustomerOrders(fetchParams));
      
      // Fetch order statistics
      orderService.getOrderStatistics(user.id)
        .then(stats => setOrderStats(stats))
        .catch(error => console.error('Failed to fetch order statistics:', error));
    }
  }, [dispatch, user?.id, currentPage, statusFilter, searchTerm]);

  // Refresh data when status filter changes
  useEffect(() => {
    setCurrentPage(0); // Reset to first page when filter changes
  }, [statusFilter]);
  
  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearCustomerError());
    };
  }, [dispatch]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleTrackOrder = async (order) => {
    setSelectedOrder(order);
    try {
      await dispatch(trackCustomerOrder(order.id)).unwrap();
      setTrackingDialogOpen(true);
    } catch (error) {
      toast.error(error || 'Failed to track order');
    }
  };

  const handleCancelOrder = (order) => {
    setSelectedOrder(order);
    setCancelDialogOpen(true);
  };

  const confirmCancelOrder = async () => {
    if (selectedOrder && cancelReason.trim()) {
      try {
        await dispatch(cancelCustomerOrder({ orderId: selectedOrder.id, reason: cancelReason })).unwrap();
        toast.success('Order cancelled successfully');
        setCancelDialogOpen(false);
        setCancelReason('');
        setSelectedOrder(null);
        // Refresh orders
        dispatch(fetchCustomerOrders({ customerId: user.id, page: currentPage, size: 10 }));
      } catch (error) {
        toast.error(error || 'Failed to cancel order');
      }
    } else {
      toast.error('Please provide a reason for cancellation');
    }
  };
  
  const handleSubmitReview = async () => {
    if (selectedProduct && rating > 0 && reviewText.trim()) {
      try {
        await dispatch(createReview({
          productId: selectedProduct.id,
          customerId: user.id,
          orderId: selectedOrder.id,
          rating,
          comment: reviewText,
          reviewType: 'PRODUCT'
        })).unwrap();
        toast.success('Review submitted successfully!');
        setReviewDialogOpen(false);
        setRating(0);
        setReviewText('');
        setSelectedProduct(null);
      } catch (error) {
        toast.error(error || 'Failed to submit review');
      }
    } else {
      toast.error('Please provide a rating and review text');
    }
  };

  const handleReviewProduct = (product) => {
    setSelectedProduct(product);
    setReviewDialogOpen(true);
    setRating(0);
    setReviewText('');
  };

  const handleMenuOpen = (event, order) => {
    setAnchorEl(event.currentTarget);
    setMenuOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuOrder(null);
  };

  const handleViewDetails = () => {
    setSelectedOrder(menuOrder);
    setOrderDetailsDialogOpen(true);
    handleMenuClose();
  };

  const handleTrackOrderFromMenu = () => {
    if (menuOrder) {
      handleTrackOrder(menuOrder);
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'processing':
        return 'primary';
      case 'shipped':
        return 'secondary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return <Pending sx={{ fontSize: 16 }} />;
      case 'confirmed':
        return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'processing':
        return <Schedule sx={{ fontSize: 16 }} />;
      case 'shipped':
        return <LocalShipping sx={{ fontSize: 16 }} />;
      case 'delivered':
        return <Done sx={{ fontSize: 16 }} />;
      case 'cancelled':
        return <Error sx={{ fontSize: 16 }} />;
      default:
        return <Assignment sx={{ fontSize: 16 }} />;
    }
  };

  const StatusBadge = ({ status }) => (
    <Chip
      icon={getStatusIcon(status)}
      label={status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase()}
      color={getStatusColor(status)}
      size="small"
      sx={{ fontWeight: 600 }}
    />
  );

  const getOrderSteps = (order) => {
    // Use tracking info if available, otherwise fall back to order data
    const trackingData = customerTrackingInfo || {};
    
    const steps = [
      { 
        label: 'Order Placed', 
        completed: true, 
        date: trackingData.orderDate || order.orderDate 
      },
      { 
        label: 'Order Confirmed', 
        completed: ['CONFIRMED', 'PROCESSING', 'SHIPPED', 'DELIVERED'].includes(order.status),
        date: trackingData.confirmedAt
      },
      { 
        label: 'Processing', 
        completed: ['PROCESSING', 'SHIPPED', 'DELIVERED'].includes(order.status),
        date: trackingData.processingAt
      },
      { 
        label: 'Shipped', 
        completed: ['SHIPPED', 'DELIVERED'].includes(order.status),
        date: trackingData.shippedAt
      },
      { 
        label: 'Delivered', 
        completed: order.status === 'DELIVERED',
        date: trackingData.deliveredAt || order.deliveredAt || order.actualDeliveryDate
      }
    ];
    
    if (order.status === 'CANCELLED') {
      return [
        { 
          label: 'Order Placed', 
          completed: true, 
          date: trackingData.orderDate || order.orderDate 
        },
        { 
          label: 'Order Cancelled', 
          completed: true,
          date: trackingData.cancelledDate || order.cancelledDate
        }
      ];
    }
    
    return steps;
  };



  // Statistics calculations
  const allOrders = customerOrders || [];
  const activeOrderStatuses = ['shipped', 'pending', 'processing'];
  const activeOrders = allOrders.filter(order => 
    activeOrderStatuses.includes(order.status?.toLowerCase())
  );
  
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const monthlyRevenue = allOrders
    .filter(order => {
      const orderDate = new Date(order.orderDate);
      return orderDate.getMonth() === currentMonth && 
             orderDate.getFullYear() === currentYear &&
             order.status?.toLowerCase() !== 'cancelled';
    })
    .reduce((total, order) => total + (order.totalAmount || 0), 0);

  const filteredOrders = allOrders.filter(order => {
    const matchesSearch = !searchTerm || 
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.items?.some(item => item.productName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      order.customerName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = false;
    if (statusFilter === 'all') {
      matchesStatus = true;
    } else if (statusFilter === 'active') {
      matchesStatus = activeOrderStatuses.includes(order.status?.toLowerCase());
    } else {
      matchesStatus = order.status?.toLowerCase() === statusFilter;
    }
    
    return matchesSearch && matchesStatus;
  });

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  const OrderCard = ({ order }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card sx={{ mb: 2, '&:hover': { boxShadow: 4 } }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Order #{order.orderNumber || order.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Placed on {new Date(order.orderDate || order.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
            <Chip
              label={order.status}
              color={getStatusColor(order.status)}
              variant="filled"
            />
          </Box>

          {/* Show rejection reason if order was rejected */}
          {order.approvalStatus === 'REJECTED' && order.approvalComments && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Order Rejected
              </Typography>
              <Typography variant="body2">
                <strong>Reason:</strong> {order.approvalComments}
              </Typography>
              {order.rejectedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Rejected on {new Date(order.rejectedAt).toLocaleString()}
                </Typography>
              )}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Order Items */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Items ({order.items?.length || 0})
            </Typography>
            {order.items?.slice(0, 2).map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2">{item.productName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Qty: {item.quantity} • ₹{item.price?.toLocaleString()}
                  </Typography>
                </Box>
                {order.status === 'DELIVERED' && (
                  <Button
                    size="small"
                    startIcon={<Star />}
                    onClick={() => handleReviewProduct(item)}
                  >
                    Review
                  </Button>
                )}
              </Box>
            ))}
            {order.items?.length > 2 && (
              <Typography variant="caption" color="text.secondary">
                +{order.items.length - 2} more items
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Order Summary */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Total: ₹{order.totalAmount?.toLocaleString()}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<Visibility />}
                onClick={() => navigate(`/orders/${order.id}`)}
              >
                View Details
              </Button>
              <Button
                size="small"
                startIcon={<LocalShipping />}
                onClick={() => handleTrackOrder(order)}
                disabled={order.status === 'CANCELLED'}
              >
                Track
              </Button>
              {['PENDING', 'CONFIRMED'].includes(order.status) && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => handleCancelOrder(order)}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Box>

          {/* Expected Delivery */}
          {order.expectedDeliveryDate && order.status !== 'DELIVERED' && order.status !== 'CANCELLED' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Expected delivery: {new Date(order.expectedDeliveryDate).toLocaleDateString()}
            </Alert>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Error Display */}
        {(customerError || fetchError) && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            onClose={() => {
              setFetchError(null);
              dispatch(clearCustomerError());
            }}
          >
            {fetchError || customerError}
          </Alert>
        )}
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3, color: '#64748b' }}>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: '#64748b',
              textDecoration: 'none',
              '&:hover': { color: '#3b82f6' }
            }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="#1e293b" fontWeight="medium">Order Management</Typography>
        </Breadcrumbs>

        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          {/* Page Title */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              fontWeight="600" 
              color="#0f172a"
              sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
            >
              Order Management
            </Typography>

          </Box>




      </Box>

      {/* Orders Data Table */}
      {loading ? (
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ mr: 2 }} />
            <Typography variant="h6">Loading orders...</Typography>
          </Box>
        </Paper>
      ) : filteredOrders.length === 0 ? (
        <Card sx={{ 
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)',
          border: '1px solid #e2e8f0',
          mt: 4
        }}>
          <CardContent sx={{ p: 8, textAlign: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              mb: 3
            }}>
              <Box sx={{ 
                p: 3, 
                backgroundColor: '#f1f5f9', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ShoppingBag sx={{ fontSize: 48, color: '#64748b' }} />
              </Box>
            </Box>
            <Typography variant="h4" fontWeight="600" color="#0f172a" sx={{ mb: 2 }}>
              No Orders Found
            </Typography>
            <Typography variant="body1" color="#64748b" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              {orders?.length === 0 
                ? "You haven't placed any orders yet. Start shopping to see your orders here!"
                : "No orders match your current filters."
              }
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/products')}
              sx={{ 
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                px: 4,
                py: 1.5,
                fontSize: '16px',
                fontWeight: '500',
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)',
                '&:hover': {
                  backgroundColor: '#2563eb',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)'
                },
                mr: 2
              }}
            >
              Start Shopping
            </Button>
            {orders?.length > 0 && (
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('active');
                }}
                sx={{
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  px: 4,
                  py: 1.5,
                  fontSize: '16px',
                  fontWeight: '500',
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': {
                    borderColor: '#3b82f6',
                    backgroundColor: '#f8fafc',
                    color: '#3b82f6'
                  }
                }}
              >
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ 
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)',
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ 
                    fontWeight: '600', 
                    fontSize: '0.875rem',
                    color: '#0f172a',
                    borderBottom: '1px solid #e2e8f0',
                    py: 2
                  }}>Order Number</TableCell>
                  <TableCell sx={{ 
                    fontWeight: '600', 
                    fontSize: '0.875rem',
                    color: '#0f172a',
                    borderBottom: '1px solid #e2e8f0',
                    py: 2
                  }}>Customer</TableCell>
                  <TableCell sx={{ 
                    fontWeight: '600', 
                    fontSize: '0.875rem',
                    color: '#0f172a',
                    borderBottom: '1px solid #e2e8f0',
                    py: 2
                  }}>Product</TableCell>
                  <TableCell align="center" sx={{ 
                    fontWeight: '600', 
                    fontSize: '0.875rem',
                    color: '#0f172a',
                    borderBottom: '1px solid #e2e8f0',
                    py: 2
                  }}>Quantity</TableCell>
                  <TableCell sx={{ 
                    fontWeight: '600', 
                    fontSize: '0.875rem',
                    color: '#0f172a',
                    borderBottom: '1px solid #e2e8f0',
                    py: 2
                  }}>Amount</TableCell>
                  <TableCell sx={{ 
                    fontWeight: '600', 
                    fontSize: '0.875rem',
                    color: '#0f172a',
                    borderBottom: '1px solid #e2e8f0',
                    py: 2
                  }}>Status</TableCell>
                  <TableCell sx={{ 
                    fontWeight: '600', 
                    fontSize: '0.875rem',
                    color: '#0f172a',
                    borderBottom: '1px solid #e2e8f0',
                    py: 2
                  }}>Date</TableCell>
                  <TableCell align="center" sx={{ 
                    fontWeight: '600', 
                    fontSize: '0.875rem',
                    color: '#0f172a',
                    borderBottom: '1px solid #e2e8f0',
                    py: 2
                  }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.slice(currentPage * 10, (currentPage + 1) * 10).map((order) => (
                  <TableRow 
                    key={order.id} 
                    hover
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8fafc' },
                      borderBottom: '1px solid #e2e8f0',
                      transition: 'background-color 0.2s ease-in-out'
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight="600" color="#3b82f6">
                        #{order.orderNumber || order.id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box>
                        <Typography variant="body2" fontWeight="500" color="#0f172a">
                          {order.customerName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="#64748b">
                          {order.shippingAddress?.city || 'Location N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight="500" color="#0f172a">
                            {order.orderItems?.[0]?.product?.name || 
                             order.items?.[0]?.productName || 
                             'Product N/A'}
                          </Typography>
                          {(order.orderItems?.length > 1 || order.items?.length > 1) && (
                            <Typography variant="caption" color="#64748b">
                              +{(order.orderItems?.length || order.items?.length || 1) - 1} more items
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Chip
                        label={
                          order.orderItems?.reduce((sum, item) => sum + item.quantity, 0) || 
                          order.items?.reduce((sum, item) => sum + item.quantity, 0) || 
                          0
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: '#e2e8f0',
                          color: '#64748b',
                          backgroundColor: '#f8fafc'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight="600" color="#0f172a">
                        ₹{order.totalAmount?.toLocaleString() || '0'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <StatusBadge status={order.status} />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight="500" color="#0f172a">
                        {new Date(order.orderDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="#64748b">
                        {new Date(order.orderDate).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Visibility />}
                          onClick={(e) => handleMenuOpen(e, order)}
                          sx={{
                            borderColor: '#e2e8f0',
                            color: '#64748b',
                            backgroundColor: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                              borderColor: '#3b82f6',
                              backgroundColor: '#f8fafc',
                              color: '#3b82f6'
                            }
                          }}
                        >
                          View
                        </Button>
                        {order.status?.toLowerCase() === 'pending' && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={() => handleCancelOrder(order)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              px: 2,
                              py: 0.5,
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            p: 3,
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc'
          }}>
            <Pagination
              count={Math.ceil(filteredOrders.length / 10)}
              page={currentPage + 1}
              onChange={(e, page) => setCurrentPage(page - 1)}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#64748b',
                  '&:hover': {
                    backgroundColor: '#f1f5f9'
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#2563eb'
                    }
                  }
                }
              }}
              showFirstButton
              showLastButton
            />
          </Box>
        </Card>
      )}

      {/* View Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <Receipt fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleTrackOrderFromMenu}>
          <ListItemIcon>
            <LocalShipping fontSize="small" />
          </ListItemIcon>
          <ListItemText>Track Order</ListItemText>
        </MenuItem>
      </Menu>

      {/* Order Tracking Dialog */}
      <Dialog
        open={trackingDialogOpen}
        onClose={() => setTrackingDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Track Order #{selectedOrder?.id}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              {/* Order Progress */}
              <Stepper activeStep={getOrderSteps(selectedOrder).findIndex(step => !step.completed)} orientation="vertical">
                {getOrderSteps(selectedOrder).map((step, index) => (
                  <Step key={step.label} completed={step.completed}>
                    <StepLabel>
                      <Box>
                        <Typography variant="subtitle1">{step.label}</Typography>
                        {step.date && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(step.date).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Shipping Information Section */}
              {['SHIPPED', 'DELIVERED'].includes(selectedOrder.status) && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocalShipping sx={{ mr: 1, color: 'primary.main' }} />
                    Shipping Information
                  </Typography>
                  <Grid container spacing={2}>
                    {(selectedOrder.trackingNumber || customerTrackingInfo?.trackingNumber) && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Tracking Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>
                            {selectedOrder.trackingNumber || customerTrackingInfo?.trackingNumber}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {(selectedOrder.shippingCarrier || customerTrackingInfo?.shippingCarrier) && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Carrier
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {selectedOrder.shippingCarrier || customerTrackingInfo?.shippingCarrier}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Tracking Information */}
              {trackingInfo && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Tracking Details
                  </Typography>
                  <List>
                    {trackingInfo.events?.map((event, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary={event.description}
                          secondary={`${event.location} • ${new Date(event.timestamp).toLocaleString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Contact Information */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Need Help?
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Button
                      fullWidth
                      startIcon={<Phone />}
                      variant="outlined"
                      href="tel:+919460061819"
                    >
                      Call Support
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      fullWidth
                      startIcon={<Email />}
                      variant="outlined"
                      href="mailto:connect@mallofstones.com"
                    >
                      Email Us
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {/* <Button
                      fullWidth
                      startIcon={<Message />}
                      variant="outlined"
                      onClick={() => toast.info('Opening chat...')}
                    >
                      Live Chat
                    </Button> */}
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to cancel order #{selectedOrder?.id}?
          </Typography>
          <TextField
            fullWidth
            label="Reason for cancellation"
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Please provide a reason for cancelling this order..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep Order</Button>
          <Button
            onClick={confirmCancelOrder}
            color="error"
            variant="contained"
          >
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Product Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Review Product</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedProduct.productName}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography component="legend">Rating</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <IconButton
                      key={star}
                      onClick={() => setRating(star)}
                      color={star <= rating ? 'primary' : 'default'}
                    >
                      {star <= rating ? <Star /> : <StarBorder />}
                    </IconButton>
                  ))}
                </Box>
              </Box>
              
              <TextField
                fullWidth
                label="Your Review"
                multiline
                rows={4}
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Share your experience with this product..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            disabled={rating === 0 || !reviewText.trim()}
          >
            Submit Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog
        open={orderDetailsDialogOpen}
        onClose={() => setOrderDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Order Details - #{selectedOrder?.orderNumber || selectedOrder?.id}
            </Typography>
            <StatusBadge status={selectedOrder?.status} />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              {/* Show rejection reason if order was rejected */}
              {selectedOrder.approvalStatus === 'REJECTED' && selectedOrder.approvalComments && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Order Rejected by Admin
                  </Typography>
                  <Typography variant="body2">
                    <strong>Rejection Reason:</strong> {selectedOrder.approvalComments}
                  </Typography>
                  {selectedOrder.rejectedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Rejected on {new Date(selectedOrder.rejectedAt).toLocaleString()}
                    </Typography>
                  )}
                </Alert>
              )}
              
              {/* Order Summary */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Order Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Order ID:</Typography>
                          <Typography variant="body2" fontWeight="600">#{selectedOrder.id}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Order Number:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedOrder.orderNumber || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Order Date:</Typography>
                          <Typography variant="body2">{new Date(selectedOrder.orderDate).toLocaleDateString()}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Total Amount:</Typography>
                          <Typography variant="body2" fontWeight="600">₹{selectedOrder.totalAmount?.toLocaleString()}</Typography>
                        </Box>
                        {selectedOrder.expectedDeliveryDate && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Expected Delivery:</Typography>
                            <Typography variant="body2">{new Date(selectedOrder.expectedDeliveryDate).toLocaleDateString()}</Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Payment Status:</Typography>
                          <Chip 
                            label={selectedOrder.paymentStatus || 'Pending'} 
                            size="small"
                            color={
                              selectedOrder.paymentStatus === 'COMPLETED' ? 'success' :
                              selectedOrder.paymentStatus === 'FAILED' ? 'error' :
                              selectedOrder.paymentStatus === 'PROCESSING' ? 'warning' : 'default'
                            }
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                          <Typography variant="body2" fontWeight="600">
                            {selectedOrder.paymentMethod || 'Cash on Delivery'}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Shipping Address
                      </Typography>
                      <Box>
                        <Typography variant="body2" fontWeight="600">
                          {selectedOrder.customerName || 'N/A'}
                        </Typography>
                        {selectedOrder.shippingAddress && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              {selectedOrder.shippingAddress.street || ''}
                            </Typography>
                            <Typography variant="body2">
                              {selectedOrder.shippingAddress.city || ''}, {selectedOrder.shippingAddress.state || ''}
                            </Typography>
                            <Typography variant="body2">
                              {selectedOrder.shippingAddress.zipCode || ''}
                            </Typography>
                            <Typography variant="body2">
                              {selectedOrder.shippingAddress.country || ''}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Order Items */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Order Items ({(selectedOrder.items || selectedOrder.orderItems)?.length || 0})
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="center">Quantity</TableCell>
                          <TableCell align="right">Selected SFT</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedOrder.items || selectedOrder.orderItems || []).map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box>
                                  <Typography variant="body2" fontWeight="600">
                                    {item.productName || item.product?.name || 'Product N/A'}
                                  </Typography>
                                  {/* <Typography variant="caption" color="text.secondary">
                                    SKU: {item.productSku || item.product?.sku || 'N/A'}
                                  </Typography> */}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={item.quantity}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              {Number(item.selectedSft) > 0 ? `${Number(item.selectedSft).toFixed(2)} SFT` : '—'}
                            </TableCell>
                            <TableCell align="right">
                              ₹{(item.price || item.unitPrice)?.toLocaleString()}
                              {Number(item.selectedSft) > 0 && !item.lotBooking && ' / SFT'}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="600">
                                ₹{Number(item.totalPrice || (item.price || item.unitPrice) * item.quantity)?.toLocaleString()}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        {(() => {
                          const orderItems = selectedOrder.items || selectedOrder.orderItems || [];
                          const itemsSubtotal = orderItems.reduce(
                            (sum, item) => sum + Number(item.totalPrice || (item.price || item.unitPrice || 0) * (item.quantity || 0)),
                            0
                          );
                          const totalAmount = Number(selectedOrder.totalAmount || 0);
                          // GST = amount actually paid minus the item totals
                          const gstAmount = Math.max(totalAmount - itemsSubtotal, 0);
                          const fmt = (v) => v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
                          return (
                            <>
                              <TableRow>
                                <TableCell colSpan={4} align="right">Items Subtotal</TableCell>
                                <TableCell align="right">₹{fmt(itemsSubtotal)}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell colSpan={4} align="right">GST</TableCell>
                                <TableCell align="right">₹{fmt(gstAmount)}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell colSpan={4} align="right">
                                  <Typography variant="subtitle2" fontWeight="700">Total Amount (incl. GST)</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2" fontWeight="700">₹{fmt(totalAmount)}</Typography>
                                </TableCell>
                              </TableRow>
                            </>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
            </Card>

            {/* Order Progress */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order Progress
                </Typography>
                <Stepper activeStep={getOrderSteps(selectedOrder).findIndex(step => !step.completed)} alternativeLabel>
                  {getOrderSteps(selectedOrder).map((step, index) => (
                    <Step key={step.label} completed={step.completed}>
                      <StepLabel>
                        <Typography variant="body2">{step.label}</Typography>
                        {step.date && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(step.date).toLocaleString()}
                          </Typography>
                        )}
                        {/* Display shipping information for shipped step */}
                        {step.shippingInfo && (step.shippingInfo.trackingNumber || step.shippingInfo.carrier) && (
                          <Box sx={{ mt: 1 }}>
                            {step.shippingInfo.trackingNumber && (
                              <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                                <LocalShipping sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                Tracking: {step.shippingInfo.trackingNumber}
                              </Typography>
                            )}
                            {step.shippingInfo.carrier && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Carrier: {step.shippingInfo.carrier}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOrderDetailsDialogOpen(false)}>Close</Button>
        <Button
          variant="outlined"
          startIcon={<LocalShipping />}
          onClick={() => {
            setOrderDetailsDialogOpen(false);
            handleTrackOrder(selectedOrder);
          }}
          disabled={selectedOrder?.status === 'CANCELLED'}
        >
          Track Order
        </Button>
        {['PENDING', 'CONFIRMED'].includes(selectedOrder?.status) && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Cancel />}
            onClick={() => {
              setOrderDetailsDialogOpen(false);
              handleCancelOrder(selectedOrder);
            }}
          >
            Cancel Order
          </Button>
        )}
      </DialogActions>
    </Dialog>
    </Container>
    </Box>
  );
};

export default OrdersPage;